/**
 * Renders the `form` core view the assistant composed (ported from the
 * MEIS-13490 prototype's `AgentFormView.tsx`): a compact structured form
 * instead of asking for several values in free text, one field per row.
 *
 * Adapted to the product's `AgentViewComponentProps` (registered as `form`
 * in `views/registry.ts`): the prototype's `isNewest`/`disabled`/
 * `answerPayload`/`onConfirm` become `answeredWith === undefined`/`busy`/
 * `extractFormAnswerPayload(answeredWith)`/`onSend(text, { confirm: true })`
 * — the same seam every other view (`AgentChoiceView`) already uses, so
 * `AgentChatBox`'s generic `ViewPart` needs no form-specific branch.
 *
 * Only the newest assistant message's form is interactive (`answeredWith
 * === undefined`), and disabled while the next answer streams in (`busy`);
 * an older message's form is read-only, showing the values the following
 * user message actually submitted (extracted from its machine block via
 * `extractFormAnswerPayload`) or, absent that, the field's own prefilled
 * value.
 *
 * An edit's field whose value differs from `previous` is highlighted with
 * the previous value struck through underneath; a create (every field's
 * `previous` is `null`) never highlights (see `isCreateForm`).
 *
 * The address field reuses the app's own address search (`AddressInput`,
 * the same component the CRM dialogs use): it looks up a real address and
 * carries its coordinates, which `validateAddressValue` then requires.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { AddressInput as AddressInputGqlType } from "../../../../clients/graphqlTypes.js";
import { AddressInput } from "../../addresses/AddressInput.js";
import {
  buildFormAnswerMessage,
  coerceFieldValue,
  extractFormAnswerPayload,
  formValueText,
  isCreateForm,
  isFieldChanged,
  validateAddressValue,
  validateFieldValue,
  validateFormView,
  ADDRESS_NOT_FROM_SEARCH_MESSAGE,
  INVALID_DATE_MESSAGE,
  INVALID_EMAIL_MESSAGE,
  INVALID_NUMBER_MESSAGE,
  INVALID_PHONE_MESSAGE,
  INVALID_POSTAL_CODE_MESSAGE,
  REQUIRED_MESSAGE,
  type Address,
  type FormField,
  type FormFieldValue,
  type FormViewSpec,
} from "./agentFormSpec.js";
import type { AgentViewComponentProps } from "./registry.js";

const SINGLE_LINE_TYPES: ReadonlySet<FormField["type"]> = new Set([
  "text",
  "email",
  "phone",
  "number",
  "date",
  "select",
]);

/**
 * `validateFieldValue`/`validateAddressValue` return one of a fixed set of
 * English messages (the module's exported `*_MESSAGE` constants), meant to
 * be shown as a field's `helperText` — each is itself a Tolgee key. Tolgee's
 * `t()` is compile-checked against the generated key union, so it cannot
 * take a plain (non-literal) `string` variable: this switches on the known
 * messages and passes each one to `t()` as a literal. The explicit `ns` on
 * every call (matching `describeRejection` in `AgentChatBox.tsx`) is load-
 * bearing: `t`'s own parameter type here is the generic, all-namespaces
 * `ReturnType<typeof useTranslate>["t"]` (unavoidable for a plain function
 * parameter), so without it a message that happens to also exist, under a
 * different role, in some other namespace's key set would compile
 * unchecked against the (unloaded) namespace it borrowed the key from.
 */
function translateFieldMessage(
  t: ReturnType<typeof useTranslate>["t"],
  message: string | null
): string | undefined {
  switch (message) {
    case null:
      return undefined;
    case REQUIRED_MESSAGE:
      return t("Required", { ns: "Assistant" });
    case INVALID_EMAIL_MESSAGE:
      return t("Invalid email address", { ns: "Assistant" });
    case INVALID_PHONE_MESSAGE:
      return t("Only digits, spaces and + - / ( ) allowed", {
        ns: "Assistant",
      });
    case INVALID_DATE_MESSAGE:
      return t("Date expected as YYYY-MM-DD", { ns: "Assistant" });
    case INVALID_NUMBER_MESSAGE:
      return t("Number expected", { ns: "Assistant" });
    case INVALID_POSTAL_CODE_MESSAGE:
      return t("Postal code: 5 digits expected", { ns: "Assistant" });
    case ADDRESS_NOT_FROM_SEARCH_MESSAGE:
      return t("Please select an address from the search", {
        ns: "Assistant",
      });
    default:
      return message;
  }
}

/** A field's value formatted for display: numbers/dates through the app's own formatting helpers, everything else through the pure `formValueText`. */
function useDisplayValue() {
  const { getFormattedFloat, getFormattedDate } = useFormatting();
  return React.useCallback(
    (field: Pick<FormField, "type" | "options">, value: FormFieldValue) => {
      if (field.type === "number" && typeof value === "number")
        return getFormattedFloat(value);
      if (field.type === "date" && typeof value === "string") {
        const date = new Date(`${value}T00:00:00Z`);
        if (!Number.isNaN(date.getTime())) return getFormattedDate(date);
      }
      return formValueText(field, value);
    },
    [getFormattedDate, getFormattedFloat]
  );
}

function PreviousValue({
  field,
  changed,
}: {
  field: FormField;
  changed: boolean;
}) {
  const { t } = useTranslate(["Assistant"]);
  const displayValue = useDisplayValue();
  if (!changed) return null;
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textDecoration: "line-through", display: "block" }}
    >
      {t("Previous: {value}", {
        ns: "Assistant",
        value: displayValue(field, field.previous),
      })}
    </Typography>
  );
}

const EMPTY_ADDRESS: Address = {
  street: "",
  postalCode: "",
  city: "",
  country: "",
};

/** `null` once every part is blank, matching `AddressInput`'s own "nothing entered yet" value. */
function toAddressInput(address: Address): AddressInputGqlType | null {
  if ([address.street, address.postalCode, address.city].every(p => !p.trim()))
    return null;
  return {
    streetLines1: address.street,
    postalCode: address.postalCode,
    city: address.city,
    countryCode: address.country,
    location: address.location ?? null,
  };
}

function fromAddressInput(value: AddressInputGqlType | null): Address {
  if (!value) return EMPTY_ADDRESS;
  return {
    street: value.streetLines1,
    postalCode: value.postalCode,
    city: value.city,
    country: value.countryCode,
    location: value.location ?? null,
  };
}

function FormFieldRow({
  field,
  value,
  changed,
  disabled,
  error,
  addressErrors,
  onChange,
  onEnter,
}: {
  field: FormField;
  value: FormFieldValue;
  changed: boolean;
  disabled: boolean;
  error: string | null;
  addressErrors: Partial<Record<keyof Address, string>>;
  onChange: (next: FormFieldValue) => void;
  onEnter: () => void;
}) {
  const { t } = useTranslate(["Assistant"]);
  const keyDown = (event: React.KeyboardEvent) => {
    if (SINGLE_LINE_TYPES.has(field.type) && event.key === "Enter") onEnter();
  };
  const changedSx = changed
    ? { borderLeft: "2px solid", borderColor: "warning.main", pl: 1 }
    : {};

  if (field.type === "address") {
    const address =
      value && typeof value === "object" && !Array.isArray(value)
        ? (value as Address)
        : EMPTY_ADDRESS;
    return (
      <Box sx={changedSx}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {field.label}
          {field.required && " *"}
        </Typography>
        {field.help && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            {field.help}
          </Typography>
        )}
        {/* The app's own address search (also used by the CRM dialogs):
            checks the address exists and adds its coordinates. */}
        <AddressInput
          label={t("Address search", { ns: "Assistant" })}
          value={toAddressInput(address)}
          error={translateFieldMessage(
            t,
            Object.values(addressErrors).find(Boolean) ?? null
          )}
          onChange={next => onChange(fromAddressInput(next))}
        />
        <PreviousValue field={field} changed={changed} />
      </Box>
    );
  }

  if (field.type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (optionValue: string) =>
      onChange(
        selected.includes(optionValue)
          ? selected.filter(v => v !== optionValue)
          : [...selected, optionValue]
      );
    return (
      <Box sx={changedSx}>
        <Typography variant="body2">
          {field.label}
          {field.required && " *"}
        </Typography>
        {field.help && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {field.help}
          </Typography>
        )}
        <Stack>
          {(field.options ?? []).map(option => (
            <FormControlLabel
              key={option.value}
              disabled={disabled}
              sx={{ my: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(option.value)}
                  onChange={() => toggle(option.value)}
                />
              }
              label={<Typography variant="body2">{option.label}</Typography>}
            />
          ))}
        </Stack>
        {error && (
          <Typography variant="caption" color="error">
            {translateFieldMessage(t, error)}
          </Typography>
        )}
        <PreviousValue field={field} changed={changed} />
      </Box>
    );
  }

  if (field.type === "select") {
    const text = typeof value === "string" ? value : "";
    return (
      <Box sx={changedSx}>
        <TextField
          select
          size="small"
          fullWidth
          label={field.label + (field.required ? " *" : "")}
          disabled={disabled}
          value={text}
          error={!!error}
          helperText={
            error ? translateFieldMessage(t, error) : (field.help ?? undefined)
          }
          onChange={event => onChange(event.target.value)}
          onKeyDown={keyDown}
        >
          {(field.options ?? []).map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <PreviousValue field={field} changed={changed} />
      </Box>
    );
  }

  const text =
    typeof value === "string" ? value : value === null ? "" : String(value);
  const inputType =
    field.type === "email"
      ? "email"
      : field.type === "phone"
        ? "tel"
        : field.type === "number"
          ? "number"
          : field.type === "date"
            ? "date"
            : "text";
  return (
    <Box sx={changedSx}>
      <TextField
        size="small"
        fullWidth
        multiline={field.type === "textarea"}
        minRows={field.type === "textarea" ? 2 : undefined}
        type={inputType}
        label={field.label + (field.required ? " *" : "")}
        disabled={disabled}
        value={text}
        error={!!error}
        helperText={
          error ? translateFieldMessage(t, error) : (field.help ?? undefined)
        }
        slotProps={
          field.type === "date" ? { inputLabel: { shrink: true } } : undefined
        }
        onChange={event => onChange(event.target.value)}
        onKeyDown={keyDown}
      />
      <PreviousValue field={field} changed={changed} />
    </Box>
  );
}

/**
 * Validates the wire spec before anything reads it: a malformed spec (e.g.
 * one without `fields` at all) renders the fallback here, so the form body's
 * hooks — which read `spec.fields` unconditionally — only ever run for a
 * spec `validateFormView` accepted.
 */
export function AgentFormView({ view, ...rest }: AgentViewComponentProps) {
  const { t } = useTranslate(["Assistant"]);
  if (validateFormView(view).length > 0)
    return (
      <Alert severity="warning" sx={{ py: 0, my: 0.5 }}>
        {t("Form could not be shown")}
      </Alert>
    );
  // `validateFormView` found no problems: `view` has the `FormViewSpec` shape.
  return <ValidAgentFormView spec={view as FormViewSpec} {...rest} />;
}

function ValidAgentFormView({
  spec,
  onSend,
  busy,
  answeredWith,
}: Omit<AgentViewComponentProps, "view"> & { spec: FormViewSpec }) {
  const displayValue = useDisplayValue();

  const isNewest = answeredWith === undefined;
  const answerPayload = isNewest
    ? null
    : extractFormAnswerPayload(answeredWith);

  const initial = React.useMemo(
    () => Object.fromEntries(spec.fields.map(f => [f.key, f.value])),
    // Only the fields' own identity should reset the draft; re-renders from
    // parent state changes (e.g. `busy` toggling) must not wipe it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spec.fields.map(f => f.key).join("|")]
  );
  const [values, setValues] =
    React.useState<Record<string, FormFieldValue>>(initial);

  const readOnly = !isNewest;
  const create = isCreateForm(spec);

  if (readOnly)
    return (
      <Stack spacing={0.5} sx={{ my: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {spec.title}
        </Typography>
        {spec.fields.map(field => {
          // Own properties only: the key is model data, and an inherited
          // name such as `constructor` must fall back to the prefilled value.
          const value =
            answerPayload && Object.hasOwn(answerPayload, field.key)
              ? answerPayload[field.key]
              : field.value;
          return (
            <Stack direction="row" spacing={1} key={field.key}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 140 }}
              >
                {field.label}
              </Typography>
              <Typography variant="body2">
                {displayValue(field, value ?? null)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    );

  const coerced: Record<string, FormFieldValue> = {};
  for (const field of spec.fields)
    coerced[field.key] = coerceFieldValue(
      field.type,
      values[field.key] ?? null
    );

  const addressErrorsByKey: Record<
    string,
    Partial<Record<keyof Address, string>>
  > = {};
  const errorByKey: Record<string, string | null> = {};
  for (const field of spec.fields) {
    if (field.type === "address") {
      const address = coerced[field.key];
      addressErrorsByKey[field.key] = validateAddressValue(
        address && typeof address === "object" && !Array.isArray(address)
          ? (address as Address)
          : null,
        field.required
      );
    } else {
      errorByKey[field.key] = validateFieldValue(field, coerced[field.key]);
    }
  }

  const isValid = spec.fields.every(field =>
    field.type === "address"
      ? Object.keys(addressErrorsByKey[field.key]).length === 0
      : errorByKey[field.key] === null
  );

  const submit = () => {
    if (!isValid || busy) return;
    onSend(buildFormAnswerMessage(spec, coerced), {
      confirm: spec.confirmsWrite,
    });
  };

  return (
    <Stack spacing={1} sx={{ my: 0.5, maxWidth: 420 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {spec.title}
      </Typography>
      {spec.description && (
        <Typography variant="caption" color="text.secondary">
          {spec.description}
        </Typography>
      )}
      {spec.fields.map(field => (
        <FormFieldRow
          key={field.key}
          field={field}
          value={values[field.key] ?? null}
          changed={
            !create && isFieldChanged(coerced[field.key], field.previous)
          }
          disabled={busy}
          error={field.type === "address" ? null : errorByKey[field.key]}
          addressErrors={addressErrorsByKey[field.key] ?? {}}
          onChange={next =>
            setValues(current => ({ ...current, [field.key]: next }))
          }
          onEnter={submit}
        />
      ))}
      <Box>
        <Button
          size="small"
          variant="contained"
          disabled={busy || !isValid}
          onClick={submit}
        >
          {spec.submitLabel}
        </Button>
      </Box>
    </Stack>
  );
}
